import React from 'react';
import { LineChart, PlusCircle, Target, BarChart3, ChevronRight } from 'lucide-react';
import { Transaction } from '../../types';
import { CashOutGoalCard } from './CashOutGoalCard';

interface EmptyDashboardProps {
  /** Real (empty) transactions — forwarded to the still-live Cash-out goal card. */
  transactions: Transaction[];
  /** Increment to pop the Cash-out goal editor (from the "Set a cash-out goal" row). */
  goalOpenSignal: number;
  onAddTransaction: () => void;
  onExploreSample: () => void;
  onSetGoal: () => void;
  onExploreMarket: () => void;
}

interface StartRow {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}

const BRONZE = '#d4b483';

/** Dashed outline "donut" (no fill) with three muted skeleton legend rows —
 *  a placeholder for an allocation chart before there's anything to allocate. */
function AllocationPlaceholder({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <div className="mb-4">
        <p className="label-caps text-slate-500">{title}</p>
        <p className="text-xs text-slate-600 mt-0.5">{subtitle}</p>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div
          className="flex-shrink-0"
          style={{
            width: 140,
            height: 140,
            borderRadius: 9999,
            border: '2px dashed rgb(var(--overlay) / 0.14)',
          }}
        />
        <div className="flex-1 w-full space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: 'rgb(var(--overlay) / 0.1)' }} />
              <span className="h-2 rounded-full flex-1" style={{ background: 'rgb(var(--overlay) / 0.07)', maxWidth: 120 }} />
              <span className="h-2 rounded-full w-10 flex-shrink-0" style={{ background: 'rgb(var(--overlay) / 0.07)' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Zero-real-transactions dashboard. Same two-column layout as the real
 * dashboard, but every data surface becomes a next-step prompt: a chart-area
 * CTA, a "Start here" action list, and dashed allocation placeholders. The
 * Cash-out goal card is kept live (it already reads well empty).
 */
export function EmptyDashboard({
  transactions,
  goalOpenSignal,
  onAddTransaction,
  onExploreSample,
  onSetGoal,
  onExploreMarket,
}: EmptyDashboardProps) {
  const rows: StartRow[] = [
    { icon: <PlusCircle className="w-4 h-4" />, title: 'Add a transaction',   subtitle: 'A buy, sell, or transfer',        onClick: onAddTransaction },
    { icon: <Target className="w-4 h-4" />,     title: 'Set a cash-out goal', subtitle: 'Work backwards from a number',     onClick: onSetGoal },
    { icon: <BarChart3 className="w-4 h-4" />,  title: 'Explore market data', subtitle: 'Top 500 coins, live',             onClick: onExploreMarket },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* Left column — chart prompt + Start here list */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Portfolio chart prompt */}
        <div
          className="flex flex-col items-center justify-center text-center"
          style={{
            border: '1px dashed rgb(var(--overlay) / 0.14)',
            borderRadius: 12,
            padding: '48px 24px',
          }}
        >
          <div
            className="flex items-center justify-center rounded-full mb-4"
            style={{ width: 44, height: 44, background: 'rgb(var(--overlay) / 0.05)' }}
          >
            <LineChart className="w-5 h-5" style={{ color: BRONZE }} />
          </div>
          <p className="text-slate-100 text-[15px] font-medium">Your portfolio value will chart here</p>
          <p className="text-slate-500 text-[13px] mt-1.5 max-w-xs">
            Log what you already hold and the history builds itself.
          </p>
          <button
            type="button"
            onClick={onAddTransaction}
            className="flex items-center gap-2 rounded-lg font-semibold mt-5 transition-colors"
            style={{ background: BRONZE, color: '#1a1410', padding: '10px 18px', fontSize: 13 }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#c9a972')}
            onMouseLeave={(e) => (e.currentTarget.style.background = BRONZE)}
          >
            <PlusCircle className="w-4 h-4" />
            Add your first transaction
          </button>
          <button
            type="button"
            onClick={onExploreSample}
            className="text-[12px] mt-3 transition-colors"
            style={{ color: 'var(--color-sub)', background: 'transparent', border: 'none', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'rgb(var(--sl-300))')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-sub)')}
          >
            or explore with sample data
          </button>
        </div>

        {/* Start here list */}
        <div>
          <p className="label-caps text-slate-500 mb-2">Start here</p>
          <div className="flex flex-col">
            {rows.map((row, i) => (
              <button
                key={row.title}
                type="button"
                onClick={row.onClick}
                className={`group flex items-center gap-3.5 py-3.5 px-2 -mx-2 rounded-lg text-left transition-colors hover:bg-[rgb(var(--overlay)/0.03)] ${i > 0 ? 'border-t border-slate-700/40' : ''}`}
              >
                <div
                  className="flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ width: 36, height: 36, background: 'rgb(var(--overlay) / 0.05)', color: BRONZE }}
                >
                  {row.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-100">{row.title}</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">{row.subtitle}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-300 transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right column — allocation placeholders + live Cash-out goal */}
      <div className="w-full lg:w-[40%] flex-shrink-0 space-y-6">
        <AllocationPlaceholder title="Asset Allocation" subtitle="Portfolio distribution" />
        <AllocationPlaceholder title="Wallet Allocation" subtitle="Distribution by wallet" />
        <div id="cash-out-goal">
          <CashOutGoalCard transactions={transactions} openSignal={goalOpenSignal} />
        </div>
      </div>
    </div>
  );
}
